import { Injectable } from '@angular/core';
import { UpgradeModule } from '@angular/upgrade/static';
import { Item } from '@app/core/components/listbox/listbox.component';
import { ErrorMessageResponse } from '@app/core/models/data-response';
import { DataTaskService } from '@app/core/services/data-task.service';

@Injectable({
    providedIn: 'root',
})
export class WebPageService {
    constructor(private datatask: DataTaskService, private upgrade: UpgradeModule) {}

    private get webpageLayout(): WebpageLayoutClient {
        return this.upgrade.$injector.get('p2WebpageService');
    }

    public async getWebPageLayoutDetails(webpageName: string): Promise<WebPageLayoutDetails> {
        return this.webpageLayout.details(webpageName);
    }

    public async toggleShowLayoutControls(webpageName: string): Promise<WebPageLayoutDetails> {
        // The existing transport updates the shared cache and broadcasts the layout change.
        return this.webpageLayout.toogleShowLayoutControls(webpageName);
    }

    /**
     * @datatask 1244
     * @datataskName sp_ws_p2_w_webpagelink_settings
     * @param params the keyno of the webpage.
     * @returns promise containing the page get for webpage link.
     */
    public async getWebPageLink(params: GetWebPageLinkParams): Promise<WebPageLinkGet> {
        return (await this.datatask.Post(1244, params))[0];
    }

    public async getP2WebpageSettings(): Promise<P2WebpageSettingsGet> {
        return (await this.datatask.Post(1184))[0];
    }

    public async listP2Webpages(): Promise<P2WebpageItem[]> {
        return this.datatask.Post(4);
    }

    public async getP2Webpage(params: GetP2WebpageParams): Promise<P2WebpageGet> {
        return (await this.datatask.Post(13, params))[0];
    }

    public async saveP2Webpage(page: P2WebpageGet): Promise<void> {
        await this.datatask.Post(15, page);
    }

    public async getP2WebpageLink(params: GetP2WebpageLinkParams): Promise<P2WebpageLinkGet> {
        return (await this.datatask.Post(17, params))[0];
    }

    public async saveP2WebpageLink(link: P2WebpageLinkGet): Promise<void> {
        await this.datatask.Post(18, link);
    }

    /**
     * @datatask 3489
     * @datataskName sp_ws_p2_w_webpagelink_set_menugrp_keyno
     * @param params the keyno of the webpage link and the menu group.
     * @returns promise containing validation for the save.
     */
    public async setWebPageLinkToMenuGroup(params: SetWebPageLinkToMenuGroupParams): Promise<ErrorMessageResponse> {
        return (await this.datatask.Post(3489, params))[0];
    }

    /**
     * @datatask 1183
     * @datataskName sp_ws_p2_w_webpagesubscription_get
     * @param params the argtype and argvalue.
     * @returns promise containing the page get for webpage subscription.
     */
    public async getWebPageSubscription(params: GetWebPageSubscriptionParams): Promise<WebPageSubscriptionGet> {
        return (await this.datatask.Post(1183, params))[0];
    }

    public async getWebPageSettingEdit(params: GetWebPageSettingEditParams): Promise<WebPageSettingEditGet> {
        return (await this.datatask.Post(19, params))[0];
    }

    public async saveWebPageSetting(page: WebPageSettingEditGet): Promise<void> {
        await this.datatask.Post(20, page);
    }

    public async getWebPageSubscriptionEdit(params: GetWebPageSubscriptionEditParams): Promise<WebPageSubscriptionEditGet> {
        return (await this.datatask.Post(83, params))[0];
    }

    public async saveWebPageSubscription(page: WebPageSubscriptionEditGet): Promise<void> {
        await this.datatask.Post(84, page);
    }

    /**
     * @datatask 22
     * @datataskName sp_ws_p2_p2webpagetable_get
     * @param params the keyno of the webpage table.
     * @returns promise containing the webpage edit.
     */
    public async getWebPageTableEdit(params: GetWebPageTableEditParams): Promise<WebPageTableEditGet> {
        return (await this.datatask.Post(22, params))[0];
    }

    /**
     * @datatask 23
     * @datataskName sp_ws_p2_p2webpagetable_save
     * @param params the webpage to save
     * @returns promise containing validation
     */
    public async saveWebPageTableEdit(params: WebPageTableEditGet): Promise<{ iserror: '1' | '0'; message: string }> {
        return (await this.datatask.Post(23, params))[0];
    }
}

export interface SetWebPageLinkToMenuGroupParams {
    menugrp_keyno: string | number;
    p2_webpagelink_keyno: string | number;
}

export interface WebPageLayoutDetails {
    show_layout_controls: string | number | boolean;
}

interface WebpageLayoutClient {
    details(webpageName: string): PromiseLike<WebPageLayoutDetails>;
    toogleShowLayoutControls(webpageName: string): PromiseLike<WebPageLayoutDetails>;
}

export interface GetWebPageLinkParams {
    p2_webpage_keyno: string | number;
}

export interface P2WebpageSettingsGet {
    searchtext: string;
}

export interface P2WebpageItem extends Item {
    item_state?: string;
}

export interface GetP2WebpageParams {
    p2_webpage_keyno: string;
}

export interface P2WebpageGet {
    p2_webpage_keyno: string;
    webpage_name: string;
    word_id: string;
    subpages: string;
}

export interface GetP2WebpageLinkParams {
    p2_webpage_keyno_belongto: string;
    p2_webpagelink_keyno: string;
}

export interface P2WebpageLinkGet {
    p2_webpagelink_keyno: string;
    p2_webpage_keyno_belongto: string;
    p2_webpage_keyno: string;
    params: string;
    orderby: number;
    word_id: string;
}

export interface WebPageLinkGet {
    searchtext: string;
    p2_webpage_keyno: string;
    menugrp_keyno: string;
    webpage_heading: string;
}

export interface GetWebPageSubscriptionParams {
    argtype: 'webpage_name' | 'p2_subscription_keyno' | 'p2_webpage_keyno';
    argvalue: string;
}

export interface WebPageSubscriptionGet {
    webpage_heading: string;
    searchtext: string;
}

export interface GetWebPageSettingEditParams {
    p2_webpage_setting_keyno: string | number;
}

export interface WebPageSettingEditGet {
    p2_webpage_setting_keyno: string;
    webpage_name_translated: string;
    webpage_name: string;
    include: '1' | '0';
}

export interface GetWebPageSubscriptionEditParams {
    p2_webpage_subscription_keyno: string | number;
}

export interface WebPageSubscriptionEditGet {
    p2_webpage_subscription_keyno: string;
    webpage_name_translated: string;
    webpage_name: string;
    access: '1' | '0';
}

export interface GetWebPageTableEditParams {
    p2_webpage_table_keyno: string | number;
}

export interface WebPageTableEditGet {
    p2_webpage_table_keyno: string;
    webpage_name: string;
    table_name: string;
    include: '1' | '0';
}
